// React Query
import {
  type UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

// Utils
import { postRequest as post } from '../utils/HttpMethods';
import * as Sentry from '@sentry/react';
import {
  claimQueryKeys,
  claimQueryResourceKeys,
  submissionQueryKeys,
} from 'api/resource';
import type {
  ElectronicClaimQueryData,
  ManitobaClaimQuerySubmissionResponse,
} from '@billing/validation/claimQuery';

export type SubmitClaimQueryRequest = ElectronicClaimQueryData & {
  claimID: string;
  siteID: string;
};

type ClaimQuerySubmissionResponse = ManitobaClaimQuerySubmissionResponse;

const submitClaimQuery = async (
  data: SubmitClaimQueryRequest
): Promise<ClaimQuerySubmissionResponse> => {
  const success: ClaimQuerySubmissionResponse = await post(
    'QBilling',
    `/action/submit/query`,
    {
      ...data,
    }
  );
  return success;
};

export const useSubmitClaimQuery = (): UseMutationResult<
  ClaimQuerySubmissionResponse,
  Error,
  SubmitClaimQueryRequest
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: submitClaimQuery,
    onError: (error) => {
      Sentry.captureException(error);
    },
    onSettled: async (_data, _error, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: claimQueryKeys.all }),
        queryClient.invalidateQueries({
          queryKey: claimQueryKeys.getClaimHistory(variables.claimID),
        }),
        queryClient.invalidateQueries({
          queryKey: claimQueryResourceKeys.all,
        }),
        queryClient.invalidateQueries({
          queryKey: submissionQueryKeys.all,
        }),
      ]);
    },
    retry: 0,
  });
};
